"use client";

import {
  FormEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { usePathname, useRouter } from "apps/website/i18n/navigation";
import axios from "axios";
import { IHeaderLink } from "apps/website/components/base/Button/Button.map";
import { useQueryParams } from "apps/website/hooks/useQueryParams";
import FlowLayout from "apps/website/layouts/Flow";
import { getDefaultLayoutData } from "apps/website/utils/content/layout";
import useErrors, { TErrors } from "apps/website/hooks/useErrors";
import { IResponseError } from "apps/website/types/form";
import { PublicConfig } from "apps/website/config/public";

import { DisplayState } from "@/constants/state";

import { LoadingSpinner } from "../../form/LoadingSpinner/LoadingSpinner";
import Section from "../../layout/Section/Section";
import Container from "../../layout/Container/Container";
import Grid from "../../layout/Grid/Grid";
import Column from "../../layout/Column/Column";
import Text from "../../base/Text/Text";
import { legacySizeCollectionMap } from "../../base/Text/Text.map";
import Spacer from "../../layout/Spacer/Spacer";
import Input from "../../form/Input/Input";
import Form from "../../form/Form/Form";
import Button from "../../base/Button/Button";
import Error from "../../form/Error/Error";

import { Fieldset } from "./layouts/FieldsetLayout";

export default function BreederContent() {

  const story = {

  };

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const [ flowGuestLinks, setFlowGuestLinks ] = useState<IHeaderLink[]>([ { id: "g-0", name: "Login", to: "/login" } ]);
  const [ hasHydrated, setHasHydrated ] = useState<boolean>(false);
  const errors = useErrors();

  const [ firstName, setFirstName ] = useState("");
  const [ lastName, setLastName ] = useState("");
  const [ GCCFPrefix, setGCCFPrefix ] = useState("");
  const [ email, setEmail ] = useState("");

  const [ sortCode, setSortCode ] = useState("");
  const [ accountNumber, setAccountNumber ] = useState("");

  const [ submitBreederContentState, setSubmitBreederContentState ] = useState<DisplayState>(DisplayState.READY);

  const layoutData = getDefaultLayoutData(story);
  const { chainQueryParamsToUrl } = useQueryParams();

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    setFlowGuestLinks(flowGuestLinks.map((link) => ({ ...link, to: chainQueryParamsToUrl(link.to) })));
  }, [ searchParams, pathname ]);

  const onSubmitNewBreederForm: FormEventHandler = useCallback(async (event) => {

    event.preventDefault();
    setSubmitBreederContentState(DisplayState.PROCESSING);
    await axios.post(`${PublicConfig.NEXT_PUBLIC_BREEDERS_API_BASE_URL}/signup`, {
      firstName,
      lastName,
      email,
      GCCFPrefix,
      sortCode,
      accountNumber,
    }).then(() => {
      router.push("/welcome/breeder");
    }).catch(({ response }) => {
      const errorsToSet: TErrors = {};
      if (response.status === 409) {
        errorsToSet.breederExists = response.data.message;
      } else {
        response.data.message.forEach((error: IResponseError) => {
          errorsToSet[error.property] = error.message[0];
        });
      }

      errors.setErrors(errorsToSet);
      setSubmitBreederContentState(DisplayState.READY);
    });
  }, [ firstName, lastName, email, GCCFPrefix, sortCode, accountNumber, router, errors ]);

  return (
    <FlowLayout
      {
        ...layoutData
      }
      headerLinks={{ ...layoutData.headerLinks, guestLinks: flowGuestLinks }}
      hideFooter={true}
    >
      <div className="bg-light-grey">
        <Section className="min-h-screen">
          <Container className="h-full">
            { hasHydrated ? (
              <Grid className="h-full">
                <Column justify="center" align="center">
                  <Text tag="h1" display="subtitle" align="center" size={legacySizeCollectionMap.titleMd}>Sign up to our KatKin Breeder Club</Text>
                  <Spacer size="xl" />

                  <Form action="" onSubmit={onSubmitNewBreederForm} className="w-full">

                    <Fieldset>
                      <Text tag="h2" display="title" size={legacySizeCollectionMap.titleSm}>Your details</Text>
                      <Spacer size="lg" />
                      <Input label="First name" name="first-name" onChange={(event) => { setFirstName(event.target.value); errors.removeError("firstName"); }} labelStyle="alternative" value={firstName} />
                      { errors.hasError("firstName") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("firstName") }</Error>
                        </>
                      ) }
                      <Spacer size="lg" />
                      <Input label="Last name" name="last-name" onChange={(event) => { setLastName(event.target.value); errors.removeError("lastName"); }} labelStyle="alternative" value={lastName} />
                      { errors.hasError("lastName") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("lastName") }</Error>
                        </>
                      ) }
                      <Spacer size="lg" />
                      <Input label="GCCF prefix" name="gccf-prefix" onChange={(event) => { setGCCFPrefix(event.target.value); errors.removeError("GCCFPrefix"); }} labelStyle="alternative" value={GCCFPrefix} />
                      { errors.hasError("GCCFPrefix") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("GCCFPrefix") }</Error>
                        </>
                      ) }
                      <Spacer size="lg" />
                      <Input type="email" label="Email address" name="email" onChange={(event) => { setEmail(event.target.value); errors.removeError("email"); }} labelStyle="alternative" value={email} />
                      { errors.hasError("email") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("email") }</Error>
                        </>
                      ) }
                      <Spacer size="2xl" />
                      <Text tag="h2" display="title" size={legacySizeCollectionMap.titleSm}><span className="text-red">*</span>Payment details</Text>
                      <Spacer size="sm" />
                      <Text tag="p" size={legacySizeCollectionMap.bodySm}><span className="text-red">*</span>These details are used to arrange payments to you. Your details will be stored securely and inline with all regulations. We will not use them for any other purpose than to pay commission due to you. If you have any concerns please contact <a href="mailto:breeders@katkin.com">breeders@katkin.com</a></Text>
                      <Spacer size="lg" />
                      <Input label="Account number" name="last-name" onChange={(event) => { setAccountNumber(event.target.value); errors.removeError("accountNumber"); }} labelStyle="alternative" value={accountNumber} />
                      { errors.hasError("accountNumber") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("accountNumber") }</Error>
                        </>
                      ) }
                      <Spacer size="lg" />
                      <Input label="Sort code" name="first-name" onChange={(event) => { setSortCode(event.target.value); errors.removeError("sortCode"); }} labelStyle="alternative" value={sortCode} />
                      { errors.hasError("sortCode") && (
                        <>
                          <Spacer size="sm" />
                          <Error>{ errors.getError("sortCode") }</Error>
                        </>
                      ) }

                    </Fieldset>
                    <Spacer size="2xl" />
                    <Grid>
                      <>
                        { errors.hasError("breederExists") && (
                          <Column>
                            <Grid>
                              <Column spans={{ lg: 6 }} offset={{ lg: 4 }}>
                                <Error>{ errors.getError("breederExists") }</Error>
                              </Column>
                            </Grid>
                          </Column>
                        ) }
                        <Column justify="center" align="center">
                          <Button type="submit" state={submitBreederContentState}>Sign up</Button>
                        </Column>
                      </>
                    </Grid>
                  </Form>
                </Column>
              </Grid>
            ) : (
              <Grid className="h-full">
                <Column justify="center" align="center">
                  <LoadingSpinner color="#000000" />
                </Column>
              </Grid>
            ) }
          </Container>
        </Section>
      </div>
    </FlowLayout>
  );
}
